import type { CompletePage, FootprintReasonCode, Language, Locale, ModernRawBusinessDataRequest, OnboardingRequirement, OnboardingSessionConfig, VaultData, WorkflowFixtureResult } from '@onefootprint/request-types';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, type ReactNode, useEffect, useRef } from 'react';
import { createStore } from 'zustand';
import { isOnboardingSuperseded } from '@/idv2/utils/is-onboarding-superseded';

/** Auth headers for creating an identify session when no token exists yet (e.g. X-Onboarding-Config-Key) */
export type ObConfigAuthHeaders = {
  'X-Onboarding-Config-Key'?: string;
  [key: string]: string | undefined;
};
export type IDV2InitialValues = {
  /** Identify/onboarding session token. Empty string when session not yet created (use obConfigAuth to create). */
  onboardingSessionToken: string;
  config: OnboardingSessionConfig;
  overallOutcome?: WorkflowFixtureResult;
  fixtureRiskSignals?: FootprintReasonCode[];
  /** ID document outcome selected on the sandbox outcomes page */
  idDocOutcome?: string;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  /** Auth headers to create identify session when onboardingSessionToken is empty (identify flow) */
  obConfigAuth?: ObConfigAuthHeaders;
  /** User auth token the tenant passed to the SDK. Identify starts its session from this user
   * instead of collecting contact info. */
  initialAuthToken?: string;
  /** Bootstrap data for KYC (id.* fields) - used in collect-data route */
  kycBootstrapData?: VaultData;
  /** Bootstrap data for KYB (business.* fields) - used in collect-business-data route */
  kybBootstrapData?: ModernRawBusinessDataRequest;
  /** Whether this session is running inside a transfer handoff (new tab opened from transfer).
   * When true, transfer should be skipped to avoid showing the QR/transfer screen again. */
  isTransfer?: boolean;
  /** Sandbox ID for sandbox mode (sent as X-Sandbox-Id when creating identify session). */
  sandboxId?: string;
  /** When true, sent as X-Fp-Is-Components-Sdk and hides "different account" option. */
  isComponentsSdk?: boolean;
};

/** Relay interface for components SDK: sends tokens to parent and listens for completion */
export type ComponentsSdkRelay = {
  relayToComponents: (tokens: {
    authToken: string;
    vaultingToken: string;
  }) => void;
  onRelayFromComponents: (cb: () => void) => () => void;
};

/** Values captured on IDV2's /sandbox-outcome route. Also relayed back to the host
 * (legacy IDV) so the host's xstate context can drive the transition to identify. */
export type SandboxOutcomeSubmitPayload = {
  overallOutcome?: WorkflowFixtureResult;
  fixtureRiskSignals?: FootprintReasonCode[];
  idDocOutcome?: string;
  sandboxId?: string;
};
export type IDV2Store = {
  initialValues: IDV2InitialValues;
  data: {
    navigation?: 'forward' | 'back';
    allRequirements: OnboardingRequirement[];
    /** Sub-page within the current requirement, reported to the host via onRequirementChange (e.g. `business_owners`) */
    requirementPage?: string;
    canGoBack: boolean;
    relayToIDV2: boolean;
    /** Whether transfer route has been visited - prevents redirect loops */
    isTransferVisited?: boolean;
    /** Identify offered this user a passkey login, so auth scope shouldn't offer to register one */
    isPasskeyRegistered?: boolean;
    /** If user clicked "Continue on mobile" in document collection — allows re-entry to transfer even after visited */
    continueOnMobile?: boolean;
    /** Set to true after relayToComponents has been called, prevents redirect loop */
    componentsSdkRelayCompleted?: boolean;
    /** The playbook's authored completion screen, stashed when the run completes on a Complete node */
    completionPage?: CompletePage;
  };
  /** Set from the QueryClient's caches; `useThrowIfSuperseded` in `__root` turns it into the
   * terminal screen. */
  supersededError?: unknown;
  onComplete: (validationToken: string, options?: {
    hasCompletionScreen?: boolean;
  }) => void;
  onHandoffComplete: () => void;
  onClose?: () => void;
  /** Set by hosts that can end the authored completion screen on a timer. */
  autoAdvance?: AutoAdvance;
  /** Called when canGoBack changes — allows the host (bifrost/inline SDK) to show/hide its back button */
  onBackButtonVisibilityChange?: (canGoBack: boolean) => void;
  /** Called when the onboarding requirement being executed changes — lets the host mirror flow progress (e.g. drive its own stepper) */
  onRequirementChange?: (payload: {
    kind: OnboardingRequirement['kind'];
    page?: string;
  }) => void;
  /** When true, hides back/close buttons in the navigation header (e.g. inline variant where the host provides its own chrome) */
  hideNavigationButtons?: boolean;
  /** Relay interface for components SDK mode — sends tokens to parent after identify */
  componentsSdkRelay?: ComponentsSdkRelay;
  /** Callback registered by AppShell to allow external back navigation (e.g. from SDK parent's ob.back()) */
  externalBack?: () => void;
  /** Called after the user submits IDV2's /sandbox-outcome form. Hosts (legacy IDV) use this
   * to mirror the selected outcome into their own state and drive the transition to the identify
   * step — IDV2 itself does not navigate forward. */
  onSandboxOutcomeSubmit?: (payload: SandboxOutcomeSubmitPayload) => void;
};

/**
 * How the completion screen continues once its countdown ends. Hosted navigates the tab to the
 * first link that opens in the same tab (and runs no countdown without one); an embedded SDK
 * cannot navigate its host page, so it closes itself instead, links or not. `mode` also picks the
 * caption's wording.
 */
export type AutoAdvance = {
  mode: 'navigate';
  onDone: (href: string) => void;
} | {
  mode: 'close';
  onDone: () => void;
};
export type Idv2ProviderProps = {
  children: ReactNode;
} & Omit<IDV2Store, 'data' | 'supersededError'>;
type StoreContextType = ReturnType<typeof createIdv2Store> | null;
export const StoreContext = createContext<StoreContextType>(null);
export const createIdv2Store = (initial: Omit<IDV2Store, 'setCurrentRequirement' | 'setHandleOnLegacyIdv'>) => createStore<IDV2Store>(() => ({
  ...initial
}));

// Store instance reference for use outside React
let currentStoreInstance: ReturnType<typeof createIdv2Store> | null = null;

/** Flow-control requirements resolve instantly and carry no user-visible progress, so they are never reported to the host */
const FLOW_CONTROL_KINDS = new Set<OnboardingRequirement['kind']>(['create_business_onboarding', 'select_conditional_branch']);
const getReportableRequirement = (state: IDV2Store): {
  kind: OnboardingRequirement['kind'];
  page?: string;
} | undefined => {
  const kind = state.data.allRequirements[0]?.kind;
  if (!kind || FLOW_CONTROL_KINDS.has(kind)) {
    return undefined;
  }
  const page = kind === 'collect_business_data' ? state.data.requirementPage : undefined;
  return page ? {
    kind,
    page
  } : {
    kind
  };
};
export const Idv2Provider = ({
  children,
  initialValues,
  onComplete,
  onClose,
  autoAdvance,
  onHandoffComplete,
  onBackButtonVisibilityChange,
  onRequirementChange,
  hideNavigationButtons,
  componentsSdkRelay,
  onSandboxOutcomeSubmit
}: Idv2ProviderProps) => {
  const storeRef = useRef<ReturnType<typeof createIdv2Store>>(undefined);
  const queryClientRef = useRef<QueryClient>(undefined);
  if (!storeRef.current) {
    storeRef.current = createIdv2Store({
      initialValues,
      data: {
        allRequirements: [],
        canGoBack: false,
        relayToIDV2: true
      },
      onComplete,
      onClose,
      autoAdvance,
      onHandoffComplete,
      onBackButtonVisibilityChange,
      onRequirementChange,
      hideNavigationButtons,
      componentsSdkRelay,
      onSandboxOutcomeSubmit
    });
    // Keep reference for use outside React
    currentStoreInstance = storeRef.current;
  }
  if (!queryClientRef.current) {
    // Every request in the flow reports a superseded session here, so no call site has to check.
    const onError = (error: unknown) => {
      if (isOnboardingSuperseded(error)) {
        storeRef.current?.setState({
          supersededError: error
        });
      }
    };
    queryClientRef.current = new QueryClient({
      mutationCache: new MutationCache({
        onError
      }),
      queryCache: new QueryCache({
        onError
      })
    });
  }
  useEffect(() => {
    return () => {
      if (currentStoreInstance === storeRef.current) {
        currentStoreInstance = null;
      }
    };
  }, []);

  // Notify host when canGoBack changes
  useEffect(() => {
    if (!onBackButtonVisibilityChange || !storeRef.current) return;
    const unsubscribe = storeRef.current.subscribe((state, prevState) => {
      if (state.data.canGoBack !== prevState.data.canGoBack) {
        onBackButtonVisibilityChange(state.data.canGoBack);
      }
    });
    return unsubscribe;
  }, [onBackButtonVisibilityChange]);

  // Notify host when the requirement being executed changes
  useEffect(() => {
    if (!onRequirementChange || !storeRef.current) return;
    const unsubscribe_0 = storeRef.current.subscribe((state_0, prevState_0) => {
      const current = getReportableRequirement(state_0);
      const previous = getReportableRequirement(prevState_0);
      if (current && (current.kind !== previous?.kind || current.page !== previous?.page)) {
        onRequirementChange(current);
      }
    });
    return unsubscribe_0;
  }, [onRequirementChange]);

  // Sync initialValues when token changes (e.g. after identify completes and idv updates authToken)
  useEffect(() => {
    storeRef.current?.setState(state_1 => ({
      ...state_1,
      initialValues: {
        ...state_1.initialValues,
        ...initialValues
      },
      // A new token is a new session, so a superseded one must not stay terminal.
      supersededError: undefined
    }));
  }, [initialValues.onboardingSessionToken]);
  return <QueryClientProvider client={queryClientRef.current}>
      <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>
    </QueryClientProvider>;
};
export const setIdv2StoreData = async (data: Partial<IDV2Store['data']>) => {
  if (!currentStoreInstance) {
    // Provider was unmounted (e.g. during story/test teardown) — skip silently.
    return;
  }
  currentStoreInstance.setState((state: IDV2Store) => ({
    ...state,
    data: {
      ...state.data,
      ...data
    }
  }));

  // Return a promise to ensure the data is set before the next action
  return Promise.resolve();
};
export const getIdv2StoreData = (): IDV2Store['data'] | null => {
  return currentStoreInstance ? currentStoreInstance.getState().data : null;
};
export const getIdv2InitialValues = (): IDV2InitialValues | null => {
  return currentStoreInstance ? currentStoreInstance.getState().initialValues : null;
};

/** Update sandbox outcome values in the store and notify the host.
 * Called from IDV2's sandbox-outcome route when the user submits the form. Writes values
 * into the IDV2 store AND fires the host's onSandboxOutcomeSubmit callback, so the host
 * (legacy IDV) can mirror the values into its own xstate context and drive the transition
 * to the identify step. IDV2 itself does not navigate forward — the host does. */
export const setSandboxOutcome = (values: SandboxOutcomeSubmitPayload) => {
  if (!currentStoreInstance) {
    throw new Error('Idv2Provider is not initialized. Store is only available after Idv2Provider mounts.');
  }
  currentStoreInstance.setState((state: IDV2Store) => ({
    ...state,
    initialValues: {
      ...state.initialValues,
      ...values
    }
  }));
  currentStoreInstance.getState().onSandboxOutcomeSubmit?.(values);
};

/** Register/unregister the external back callback (called by AppShell to sync navigation.back into the store) */
export const setExternalBack = (back: (() => void) | undefined) => {
  currentStoreInstance?.setState({
    externalBack: back
  });
};

/** Trigger back navigation inside IDV2's TanStack router from outside (e.g. legacy IDV router forwarding SDK parent's ob.back()) */
export const callExternalBack = () => {
  currentStoreInstance?.getState().externalBack?.();
};

/** Call onHandoffComplete from outside React (in route beforeLoad).
 * Used when desktop new tab transfer session needs to complete the D2P session and close
 * for the parent window to re-enter the transfer flow to show a qr code for the user to scan with mobile */
export const callOnHandoffComplete = () => {
  currentStoreInstance?.getState().onHandoffComplete();
};

/** Update the identify/onboarding session token (e.g. after creating session with obConfigAuth in identify flow) */
export const setOnboardingSessionToken = (token: string) => {
  if (!currentStoreInstance) {
    throw new Error('Idv2Provider is not initialized. Store is only available after Idv2Provider mounts.');
  }
  currentStoreInstance.setState((state: IDV2Store) => ({
    ...state,
    initialValues: {
      ...state.initialValues,
      onboardingSessionToken: token
    }
  }));
};